<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { api } from '@/api/client'
import type { AdminConventionRow, ConventionOverrideStatus } from '@/api/adminTypes'

// Grouped admin sidebar (ports ghcboot's AdminNav grouping idea into the Broadsheet voice):
// flat sections with ink rules instead of hover dropdowns. Sections mirror how the desk is
// staffed — Events, People, Commerce, Content, Tools.
//
// It also hosts the "way back" convention switcher (ghcboot's ConventionContext port): a
// session-scoped override that lets an admin work as a past convention. While overridden, a
// warning banner with a one-click reset sits at the top of every admin page; the whole app
// (admin and public pages alike) follows the override because the backend's getCurrentCon()
// resolves it per session.
interface NavLink {
  label: string
  to: string
}
interface NavSection {
  head: string
  links: NavLink[]
}

// Mobile disclosure: at narrow widths the sidebar collapses behind an "Admin menu" toggle so it
// no longer squeezes the page. Desktop is unaffected (CSS keeps the nav always visible there).
const route = useRoute()
const open = ref(false)
const toggleRef = ref<HTMLButtonElement | null>(null)

// Any navigation (a link click or a programmatic push) closes the drawer.
watch(
  () => route.fullPath,
  () => {
    open.value = false
  },
)

// Escape closes the drawer and returns focus to the toggle for a sensible focus path.
function onEsc() {
  if (!open.value) return
  open.value = false
  toggleRef.value?.focus()
}

const sections: NavSection[] = [
  {
    head: 'Desk',
    links: [{ label: 'Dashboard', to: '/admin' }],
  },
  {
    head: 'Events',
    links: [
      { label: 'Event queue', to: '/admin/events' },
      { label: 'Search', to: '/admin/events/search' },
      { label: 'Slots', to: '/admin/slots' },
      { label: 'Schedule grid', to: '/admin/schedule' },
      { label: 'Tables', to: '/admin/tables' },
    ],
  },
  {
    head: 'People',
    links: [
      { label: 'Registrations', to: '/admin/registrations' },
      { label: 'Users', to: '/admin/users' },
      { label: 'Guests', to: '/admin/guests' },
      { label: 'Groups', to: '/admin/groups' },
      { label: 'Form responses', to: '/admin/form-responses' },
      { label: 'Activity', to: '/admin/activity' },
    ],
  },
  {
    head: 'Commerce',
    links: [
      { label: 'Registration types', to: '/admin/regtypes' },
      { label: 'Registration codes', to: '/admin/regcodes' },
      { label: 'Promo codes', to: '/admin/promocodes' },
      { label: 'Transactions', to: '/admin/transactions' },
      { label: 'Dealer applications', to: '/admin/dealer-applications' },
      { label: 'Exhibitors', to: '/admin/exhibitors' },
    ],
  },
  {
    head: 'Content',
    links: [{ label: 'Content blocks', to: '/admin/content' }],
  },
  {
    head: 'Tools',
    links: [
      { label: 'Conventions', to: '/admin/conventions' },
      { label: 'Reindex · reports · tickets', to: '/admin/tools' },
      { label: 'Style guide', to: '/admin/style-guide' },
    ],
  },
]

// ---- "Way back" convention switcher ----------------------------------------------------------
const override = ref<ConventionOverrideStatus | null>(null)
const conventions = ref<AdminConventionRow[]>([])
const selectedConID = ref<number | ''>('')
const switching = ref(false)

onMounted(async () => {
  try {
    const [status, cons] = await Promise.all([
      api.get<ConventionOverrideStatus>('/api/v1/admin/convention-override'),
      api.get<AdminConventionRow[]>('/api/v1/admin/conventions'),
    ])
    override.value = status
    conventions.value = cons
    selectedConID.value = status.conID ?? ''
  } catch {
    // Selector stays hidden; the rest of the nav still works.
  }
})

// Every open page holds con-scoped data fetched under the previous convention, so the only
// honest refresh after a switch is a full reload (same as ghcboot's redirect-back-to-referer).
function reloadApp() {
  window.location.reload()
}

async function applyOverride() {
  if (selectedConID.value === '' || switching.value) return
  if (override.value && selectedConID.value === override.value.conID) return
  switching.value = true
  try {
    override.value = await api.put<ConventionOverrideStatus>('/api/v1/admin/convention-override', {
      conID: selectedConID.value,
    })
    reloadApp()
  } catch {
    selectedConID.value = override.value?.conID ?? ''
    switching.value = false
  }
}

async function resetOverride() {
  if (switching.value) return
  switching.value = true
  try {
    override.value = await api.del<ConventionOverrideStatus>('/api/v1/admin/convention-override')
    reloadApp()
  } catch {
    switching.value = false
  }
}
</script>

<template>
  <div class="adm-nav-wrap" @keydown.escape="onEsc">
    <button
      ref="toggleRef"
      type="button"
      class="adm-nav-toggle"
      aria-controls="adm-nav-menu"
      :aria-expanded="open"
      data-test="admin-nav-toggle"
      @click="open = !open"
    >
      <span class="adm-nav-toggle-bars" aria-hidden="true"></span>
      <span>{{ open ? 'Close menu' : 'Admin menu' }}</span>
    </button>
    <nav
      id="adm-nav-menu"
      class="adm-nav"
      :class="{ 'is-open': open }"
      aria-label="Admin sections"
    >
    <div v-if="override" class="adm-nav-section adm-workingas" data-test="working-as">
      <p class="adm-nav-head">Working as</p>
      <select
        v-model="selectedConID"
        class="select workingas-select"
        :disabled="switching"
        aria-label="Working as convention"
        data-test="working-as-select"
        @change="applyOverride"
      >
        <option v-for="c in conventions" :key="c.conID" :value="c.conID">
          {{ c.conName }}{{ c.current ? ' (current)' : '' }}
        </option>
      </select>
      <div v-if="override.active" class="workingas-warning" role="alert" data-test="override-banner">
        <p>
          <strong>Way back:</strong> viewing <strong>{{ override.conName }}</strong> — data on
          every page is for that convention, not {{ override.actualConName }}.
        </p>
        <button
          class="btn-text"
          type="button"
          :disabled="switching"
          data-test="override-reset"
          @click="resetOverride"
        >
          Back to {{ override.actualConName }}
        </button>
      </div>
    </div>

    <div v-for="section in sections" :key="section.head" class="adm-nav-section">
      <p class="adm-nav-head">{{ section.head }}</p>
      <ul>
        <li v-for="link in section.links" :key="link.to">
          <!-- /admin is a prefix of every admin path, so the Dashboard link only highlights on
               an exact match; the rest use the default prefix matching. -->
          <RouterLink
            :to="link.to"
            :active-class="link.to === '/admin' ? 'adm-noop' : undefined"
            :exact-active-class="link.to === '/admin' ? 'router-link-active' : undefined"
          >
            {{ link.label }}
          </RouterLink>
        </li>
      </ul>
    </div>
    </nav>
  </div>
</template>

<style scoped>
.workingas-select {
  width: 100%;
  font-size: 13px;
}
.workingas-warning {
  margin-top: 8px;
  padding: 8px 10px;
  border: 1px solid var(--danger, #a33);
  background: rgba(170, 51, 51, 0.08);
  font-size: 12.5px;
  line-height: 1.4;
}
.workingas-warning p {
  margin: 0 0 6px;
}
</style>
